/**
 * Treatment Coordinator in-app manual. Keep in sync with docs/manuals/TREATMENT_COORDINATOR_MANUAL.md
 */

import { createHelpAssistantManualSection } from "./helpAssistantManualSection.js";

export const TREATMENT_COORDINATOR_MANUAL_INTRO = {
  title: "Treatment Coordinator guide",
  subtitle:
    "Treatment Coordinators run consultations, clinical documentation, follow-ups, pipeline work, and scheduling tasks. **Treatments** and **Stages** admin screens are **owner-only** in the sidebar (and routes), even though your role may include catalog permissions for other flows. Use the **header** location switcher for multi-site orgs. **Settings** gear in the sidebar is **owner-only**; use the **user menu** for **Your Profile**.",
  defaultRoute: "/dashboard",
};

/** @typedef {{ id: string, group: 'menu' | 'settings' | 'more', menuLabel: string, route: string, keywords: string[], summary: string, howToUse: string[] }} TcManualSection */

/** @type {TcManualSection[]} */
export const TREATMENT_COORDINATOR_MANUAL_SECTIONS = [
  {
    id: "dashboard",
    group: "menu",
    menuLabel: "Dashboard",
    route: "/dashboard",
    keywords: ["home", "overview", "tc"],
    summary: "Practice overview and TC-focused widgets for your location context.",
    howToUse: [
      "Open **Dashboard** (`/dashboard`).",
      "Confirm **location** in the header when you switch offices.",
    ],
  },
  {
    id: "appointments",
    group: "menu",
    menuLabel: "Appointments",
    route: "/appointments",
    keywords: ["schedule", "calendar"],
    summary: "Appointments for coordination and same-day consultation prep.",
    howToUse: [
      "Click **Appointments** (`/appointments`).",
      "Use the list to align with consults and follow-ups.",
    ],
  },
  {
    id: "consultations",
    group: "menu",
    menuLabel: "Consultations",
    route: "/consultations",
    keywords: [
      "consultation",
      "consultations",
      "recording",
      "soap",
      "transcript",
      "start",
      "take",
      "download",
      "export",
      "report",
      "excel",
    ],
    summary:
      "Run and review chairside consultations: start recording from Appointments or Recording, then review the list and detail pages. Export SOAP notes individually or in bulk.",
    howToUse: [
      "To **take** a consultation: open **Appointments** (`/appointments`) and launch recording for the visit, or go to **Recording** (`/recording`) or `/recording/:appointmentId`.",
      "Complete consent and microphone checks on the recording wizard, then record the consult.",
      "After the visit, open **Consultations** (`/consultations`) and open a row at `/consultations/:id` for transcript, SOAP, and recordings.",
      "To **download one SOAP note**: on consultation detail, click **Export SOAP Note** (`.txt` file).",
      "To **download a SOAP report (Excel)**: on **Consultations**, set the date range and click **Download SOAP** (`.xlsx` bulk export).",
    ],
    faqs: [
      {
        question: "How do I take a consultation?",
        answer:
          "Launch [[Recording|/recording]] from **Appointments** or directly, complete consent checks, record the visit, then review it under [[Consultations|/consultations]].",
      },
      {
        question: "How do I download a SOAP report?",
        answer:
          "Bulk: [[Consultations|/consultations]] → Download SOAP (.xlsx). Single note: open /consultations/:id → Export SOAP Note (.txt).",
      },
    ],
  },
  {
    id: "pipeline",
    group: "menu",
    menuLabel: "Pipeline",
    route: "/pipeline",
    keywords: ["opportunities", "acceptance", "cases", "kanban", "move", "stage"],
    summary:
      "Treatment pipeline kanban: move patients between stages, add notes, and create follow-ups.",
    howToUse: [
      "Open **Pipeline** (`/pipeline`).",
      "Select a treatment plan; click patient cards to update notes or move to another stage.",
      "Notes are required when changing stage. Create follow-ups from cards when needed.",
    ],
    faqs: [
      {
        question: "How do I move a patient in the pipeline?",
        answer:
          "[[Pipeline|/pipeline]] → click patient → choose new stage → enter notes → save.",
      },
    ],
  },
  {
    id: "follow-ups",
    group: "menu",
    menuLabel: "Follow-Ups",
    route: "/follow-ups",
    keywords: ["outreach", "nurture", "calendar", "overdue", "complete"],
    summary:
      "Follow-up calendar for scheduled patient outreach. View overdue items and mark complete.",
    howToUse: [
      "Choose **Follow-Ups** (`/follow-ups`).",
      "Use the calendar and overdue banner; open rows to complete or reschedule.",
      "Click Refresh to reload the list.",
    ],
    faqs: [
      {
        question: "How do I complete a follow-up?",
        answer:
          "Open [[Follow-Ups|/follow-ups]], select the item, and mark it complete with any required notes.",
      },
    ],
  },
  {
    id: "support",
    group: "menu",
    menuLabel: "Support",
    route: "/tickets",
    keywords: ["ticket", "help"],
    summary: "Org support tickets.",
    howToUse: [
      "Click **Support** (`/tickets`).",
      "Log issues that need platform or success team help.",
    ],
  },
  {
    id: "profile",
    group: "settings",
    menuLabel: "Your Profile",
    route: "/settings/profile",
    keywords: ["password", "npi", "title", "credentials"],
    summary:
      "Personal profile; owners and TCs may edit professional title and related fields per product rules.",
    howToUse: [
      "Avatar menu → **Your Profile** (`/settings/profile`).",
      "Update credentials or password as allowed.",
    ],
  },
  {
    id: "settings-hub",
    group: "settings",
    menuLabel: "Settings (hub)",
    route: "/settings",
    keywords: ["gear"],
    summary:
      "Hub is oriented to **owner** org management; TCs typically rely on **Your Profile**.",
    howToUse: [
      "Open `/settings` from the user menu if needed.",
      "Expect limited cards compared to an owner account.",
    ],
  },
  {
    id: "recording",
    group: "more",
    menuLabel: "Recording",
    route: "/recording",
    keywords: ["chairside", "consent", "capture", "microphone", "pause", "stop"],
    summary:
      "Chairside recording wizard: consent, mic checks, record, pause, and stop to create a consultation.",
    howToUse: [
      "Use `/recording` or `/recording/:appointmentId` from an appointment flow.",
      "Complete consent and microphone checks, then record the consult.",
      "Pause if needed; stop when done. Review under [[Consultations|/consultations]].",
    ],
    faqs: [
      {
        question: "How do I record a consultation?",
        answer:
          "Launch [[Recording|/recording]] from [[Appointments|/appointments]], complete consent/mic checks, record, stop. Find the visit in [[Consultations|/consultations]].",
      },
    ],
  },
  {
    id: "smile-preview",
    group: "more",
    menuLabel: "Smile Preview",
    route: "/smile-preview",
    keywords: ["ai", "cosmetic", "imaging"],
    summary: "AI smile visualization for case conversations.",
    howToUse: [
      "Open `/smile-preview`.",
      "Follow upload or selection steps on the page.",
    ],
  },
  {
    id: "coaching",
    group: "more",
    menuLabel: "Coaching",
    route: "/coaching",
    keywords: ["scorecard", "feedback"],
    summary: "Consultation coaching / scorecard insights when generated for your org.",
    howToUse: [
      "Open `/coaching`.",
      "Review feedback tied to consultation recordings.",
    ],
  },
  createHelpAssistantManualSection(),
];

export const TREATMENT_COORDINATOR_MANUAL_GROUPS = {
  menu: {
    label: "Main menu",
    description: "TC sidebar items.",
  },
  settings: {
    label: "Account & settings",
    description: "Profile and settings hub (sidebar gear is owner-only).",
  },
  more: {
    label: "More features",
    description: "Deep links for recording, smile preview, tasks, and messaging.",
  },
};

export function filterTreatmentCoordinatorManualSections(query, groupFilter) {
  const q = (query || "").trim().toLowerCase();
  return TREATMENT_COORDINATOR_MANUAL_SECTIONS.filter((s) => {
    if (groupFilter && groupFilter !== "all" && s.group !== groupFilter) {
      return false;
    }
    if (!q) return true;
    const hay = [
      s.menuLabel,
      s.summary,
      s.route,
      ...s.keywords,
      ...s.howToUse.join(" "),
      ...(s.faqs || []).map((f) => `${f.question} ${f.answer}`).join(" "),
    ]
      .join(" ")
      .toLowerCase();
    return hay.includes(q);
  });
}
